import { useState, useRef, useEffect } from 'react';
import { Button } from "@themesberg/react-bootstrap";

export default function SignaturePad({ sendSignature, loading }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signature, setSignature] = useState(null);
  const [canvasSize, setCanvasSize] = useState({ width: 500, height: 200 });

  // Prevent scrolling when drawing
  useEffect(() => {
    const preventScroll = (e) => {
      if (isDrawing) {
        e.preventDefault();
      }
    };

    document.addEventListener('touchmove', preventScroll, { passive: false });
    return () => {
      document.removeEventListener('touchmove', preventScroll);
    };
  }, [isDrawing]);

  // Initialize canvas when component mounts
  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Set initial canvas style
    context.lineWidth = 2;
    context.strokeStyle = '#000000';
    context.lineJoin = 'round';
    context.lineCap = 'round';

    // Clear canvas
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);

    // Handle window resize
    const handleResize = () => {
      const container = canvas.parentElement;
      const width = Math.min(container.clientWidth - 20, 500); // Max width 500px
      setCanvasSize({ width, height: 200 });
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update canvas size when canvasSize changes
  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Save current drawing
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);

    // Update canvas size
    canvas.width = canvasSize.width;
    canvas.height = canvasSize.height;

    // Restore drawing
    context.putImageData(imageData, 0, 0);
  }, [canvasSize]);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    let clientX, clientY;

    if (e.type.includes('touch')) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    context.beginPath();
    context.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;

    const { x, y } = getCoordinates(e);
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    context.lineTo(x, y);
    context.stroke();
  };

  const endDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    context.closePath();
    setIsDrawing(false);

    // Save signature as data URL
    const dataUrl = canvas.toDataURL('image/png');
    setSignature(dataUrl);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    setSignature(null)
  };

  const onSave = () => {
    sendSignature(signature)
  }


  return (
    <div className="flex flex-col items-center w-full max-w-xl mx-auto">
      <div className="border-2 border-black rounded-md mb-4 relative w-full overflow-hidden shadow-md">
        <canvas
          ref={canvasRef}
          width={canvasSize.width}
          height={canvasSize.height}
          className="bg-white cursor-crosshair touch-none w-full select-none border border-gray-300"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={endDrawing}
          onMouseLeave={endDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={endDrawing}
          style={{ touchAction: 'none' }}
        />
        <div className="absolute top-2 left-2 text-sm text-gray-500">Firma aquí</div>
      </div>

      <div className="flex space-x-4 mb-4">
        <Button
          variant='secondary'
          onClick={clearSignature}
          className="m-2"
          disabled={loading}
        >
          Clear
        </Button>
        <Button
          disabled={!signature || loading}
          onClick={onSave}
          className="m-2"
          variant='primary'
        >
          {loading ? 'Enviando...' : 'Enviar'}
        </Button>
      </div>
    </div>
  );
}